import React, { useEffect, useRef, useState } from 'react';
import { Button, Dropdown, Form, Icon, Label, Loader, Message } from 'semantic-ui-react';
import { useTranslation } from 'react-i18next';
import { API } from '../helpers';
import { commercialBaseV106, commercialOptionsV106, unwrapCommercialV106 } from '../pages/CommercialV106/shared';
import { CommercialPriceVectorV106, PriceOfferSelectorV106 } from '../pages/CommercialV106/PriceOfferSelectorV106';
import SourceQuotesV106 from '../pages/CommercialV106/SourceQuotesV106';
import '../pages/CommercialV106/commercial.css';

function CustomerModelV106({ item }) {
  const [selected, setSelected] = useState('');
  const offer = item.offers?.find((value) => value.id === selected) || item.offers?.[0];
  return (
    <section className='commercial-preview-model-v106'>
      <h3>{item.display_name || item.model_name}</h3>
      <PriceOfferSelectorV106 offers={item.offers} selected={selected} onChange={setSelected} />
      <CommercialPriceVectorV106 offer={offer || { customer_prices: item.customer_prices }} />
      {item.source_quotes && <SourceQuotesV106 quotes={item.source_quotes} />}
    </section>
  );
}

function CustomerDrawerV106({ onClose }) {
  const { i18n } = useTranslation();
  const zh = i18n?.language?.startsWith('zh');
  const [organizations, setOrganizations] = useState([]);
  const [members, setMembers] = useState([]), [keys, setKeys] = useState([]);
  const [accountID, setAccountID] = useState(''), [userID, setUserID] = useState(''), [tokenID, setTokenID] = useState('');
  const [data, setData] = useState(null), [busy, setBusy] = useState(false), [error, setError] = useState('');
  const epoch = useRef(0), drawer = useRef(null);
  useEffect(() => {
    let cancelled = false;
    API.get(commercialBaseV106 + '/schema').then((response) => {
      if (!cancelled) setOrganizations(unwrapCommercialV106(response).organizations || []);
    }).catch((failure) => { if (!cancelled) setError(failure.message); });
    drawer.current?.querySelector('button')?.focus();
    return () => { cancelled = true; epoch.current += 1; };
  }, []);
  useEffect(() => {
    let cancelled = false;
    epoch.current += 1;
    setMembers([]); setKeys([]); setUserID(''); setTokenID(''); setData(null); setBusy(false); setError('');
    if (!accountID) return undefined;
    API.get(`/api/billing/v8/admin/organizations/${accountID}`).then((response) => {
      if (!cancelled) setMembers(unwrapCommercialV106(response).members || []);
    }).catch((failure) => { if (!cancelled) setError(failure.message); });
    return () => { cancelled = true; epoch.current += 1; };
  }, [accountID]);
  useEffect(() => {
    let cancelled = false;
    epoch.current += 1;
    setKeys([]); setTokenID(''); setData(null); setBusy(false);
    if (!userID) return undefined;
    API.get(`/api/token/managed/${userID}/`, { params: { page_size: 1000 } }).then((response) => {
      if (!cancelled) setKeys(unwrapCommercialV106(response) || []);
    }).catch((failure) => { if (!cancelled) setError(failure.message); });
    return () => { cancelled = true; epoch.current += 1; };
  }, [userID]);
  const load = async () => {
    const identity = ++epoch.current;
    setBusy(true); setData(null); setError('');
    try {
      const response = await API.get(commercialBaseV106 + '/customer-view', { params: { account_id: accountID, user_id: userID, token_id: tokenID || undefined } });
      if (identity === epoch.current) setData(unwrapCommercialV106(response));
    } catch (failure) { if (identity === epoch.current) setError(failure.message); }
    finally { if (identity === epoch.current) setBusy(false); }
  };
  return (
    <aside ref={drawer} className='commercial-preview-v106' aria-label={zh ? '客户只读视图' : 'Readonly customer view'}
      onKeyDown={(event) => { if (event.key === 'Escape') onClose(); }}>
      <div className='commercial-preview-heading-v106'>
        <h2>{zh ? '客户只读视图' : 'Readonly customer view'}</h2>
        <Button type='button' icon basic aria-label={zh ? '关闭客户视图' : 'Close customer projection'} onClick={onClose}><Icon name='close' /></Button>
      </div>
      <Label basic>{zh ? '只读' : 'Read only'}</Label>
      <Form>
        <Form.Field><label>{zh ? '组织' : 'Organization'}</label>
          <Dropdown search selection fluid options={commercialOptionsV106(organizations)} value={accountID}
            onChange={(_, { value }) => { epoch.current += 1; setAccountID(value); setData(null); }} />
        </Form.Field>
        <Form.Field><label>{zh ? '成员' : 'Member'}</label>
          <Dropdown search selection fluid disabled={!accountID} options={members.map((member) => ({
            key: member.user?.id || member.user_id, value: member.user?.id || member.user_id,
            text: member.user?.display_name || member.user?.username || String(member.user_id),
          }))} value={userID} onChange={(_, { value }) => { epoch.current += 1; setUserID(value); setData(null); }} />
        </Form.Field>
        <Form.Field><label>Key</label>
          <Dropdown search selection clearable fluid disabled={!userID} options={commercialOptionsV106(keys)} value={tokenID}
            onChange={(_, { value }) => { epoch.current += 1; setTokenID(value); setData(null); setBusy(false); }} />
        </Form.Field>
        <Button type='button' primary disabled={busy || !accountID || !userID} loading={busy} onClick={load}>
          <Icon name='eye' />{zh ? '查看客户价格' : 'View customer pricing'}
        </Button>
      </Form>
      {error && <Message negative content={error} />}
      {busy && <Loader active inline='centered' />}
      {data?.models?.length === 0 && <Message content={zh ? '没有可用模型' : 'No available models'} />}
      {data?.models?.map((item) => <CustomerModelV106 key={`${data.user_id}:${data.token_id}:${item.model_name}`} item={item} />)}
    </aside>
  );
}

export default function CommercialPreviewV106({ root = false }) {
  const { i18n } = useTranslation();
  const zh = i18n?.language?.startsWith('zh');
  const [open, setOpen] = useState(false);
  const button = useRef(null);
  const opener = useRef(null);
  useEffect(() => {
    if (!root) return undefined;
    const openCustomerView = () => { opener.current = document.activeElement; setOpen(true); };
    window.addEventListener('commercial:open-customer-preview', openCustomerView);
    return () => window.removeEventListener('commercial:open-customer-preview', openCustomerView);
  }, [root]);
  if (!root) return null;
  const close = () => { setOpen(false); (opener.current || button.current)?.focus(); opener.current = null; };
  return (
    <>
      <button ref={button} type='button' className='ac-icon-button-v9'
        title={zh ? '客户只读视图' : 'Readonly customer view'} aria-label={zh ? '客户只读视图' : 'Readonly customer view'}
        aria-expanded={open} onClick={() => setOpen((current) => !current)}><Icon name='users' /></button>
      {open && <CustomerDrawerV106 onClose={close} />}
    </>
  );
}
